import type { ComponentProps, ReactNode } from 'react';

import { useTranslation } from 'react-i18next';

import ArrowUpDownIcon from '@crm/shared/assets/icons/arrow-up-down.svg?react';
import DownIcon from '@crm/shared/assets/icons/down.svg?react';
import UpIcon from '@crm/shared/assets/icons/up.svg?react';
import { cn } from '@crm/shared/lib';

import { getNextTableSortState, type TableSortDirection } from './getNextTableSortState';
import { TableHead } from './Table';
import { SvgIcon } from '../SvgIcon';

type SortableTableHeadAlign = 'start' | 'center';
type SortViewState = TableSortDirection | 'default';

const getSortPresentation = (state: SortViewState, allowClear: boolean) => {
    switch (state) {
        case 'asc':
            return {
                ariaSort: 'ascending' as const,
                icon: UpIcon,
                titleKey: 'crm.common.dealsTable.sort.descending' as const,
            };
        case 'desc':
            return {
                ariaSort: 'descending' as const,
                icon: DownIcon,
                titleKey: allowClear
                    ? ('crm.common.dealsTable.sort.clear' as const)
                    : ('crm.common.dealsTable.sort.ascending' as const),
            };
        default:
            return {
                ariaSort: undefined,
                icon: ArrowUpDownIcon,
                titleKey: 'crm.common.dealsTable.sort.ascending' as const,
            };
    }
};

type SortableTableHeadProps<TSortField extends string> = Omit<
    ComponentProps<typeof TableHead>,
    'align' | 'aria-sort' | 'children'
> & {
    activeSort: TSortField | null;
    align?: SortableTableHeadAlign;
    allowClear: boolean;
    children: ReactNode;
    direction: TableSortDirection | null;
    onSortChange: (sortState: {
        direction: TableSortDirection | null;
        sort: TSortField | null;
    }) => void;
    sortField: TSortField;
};

export const SortableTableHead = <TSortField extends string>({
    activeSort,
    align = 'start',
    allowClear,
    children,
    className,
    direction,
    onSortChange,
    sortField,
    ...props
}: SortableTableHeadProps<TSortField>) => {
    const { t } = useTranslation();
    const sortState: SortViewState = activeSort === sortField && direction ? direction : 'default';
    const { ariaSort, icon, titleKey } = getSortPresentation(sortState, allowClear);
    const title = t(titleKey);

    const handleSortChange = () => {
        onSortChange(
            getNextTableSortState({
                allowClear,
                direction,
                sort: activeSort,
                sortField,
            })
        );
    };

    return (
        <TableHead
            {...props}
            className={cn('px-1', align === 'center' && 'text-center', className)}
            aria-sort={ariaSort}
        >
            <button
                type="button"
                className={cn(
                    'flex size-full cursor-pointer items-center gap-1 hover:text-smtr-main-black-100',
                    align === 'center' ? 'justify-center' : 'justify-start'
                )}
                title={title}
                onClick={handleSortChange}
            >
                {children}
                <SvgIcon icon={icon} size={12} />
            </button>
        </TableHead>
    );
};
